import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { estApi } from "../lib/api";

const KIND = { bid: "Bid", estimate: "Estimate", pipeline: "Pipeline", budget: "Budget", bod_row: "Basis of Design", po: "PO", email: "Email" };
const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "");

/** Directory › company History: everything this company touches, with hit rates. Backed by the party_links ledger. */
export default function CompanyHistory({ companyId }) {
  const [p, setP] = useState(null);
  useEffect(() => { if (companyId) estApi.companyProfile(companyId).then(setP).catch(() => setP({ timeline: [], counts: {} })); }, [companyId]);
  if (!companyId) return null;
  if (!p) return <div className="flex items-center gap-2 py-2 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> Loading history…</div>;
  const stat = (label, v, testid) => <div className="rounded-sm border border-slate-200 px-2 py-1" data-testid={testid}><div className="text-[9px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-sm font-semibold tabular-nums">{v}</div></div>;
  return (
    <div className="px-5 pb-3" data-testid="company-history">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">History</span>
        <span className="text-[10px] text-slate-400">{Object.entries(p.counts).map(([k, n]) => `${n} ${KIND[k] || k}${n === 1 ? "" : "s"}`).join(" · ") || "No records yet"}</span>
      </div>
      <div className="mb-2 flex flex-wrap gap-2">
        {stat("Bids", p.counts.bid || 0, "company-history-bids")}
        {stat("Won / Lost", `${p.hit_rate.won} / ${p.hit_rate.lost}`, "company-history-wl")}
        {stat("Hit rate", p.hit_rate.rate === null ? "—" : `${p.hit_rate.rate}%`, "company-history-hit")}
        {p.bod_rate.rows > 0 && stat("BOD / AE rate", `${p.bod_rate.rate}%`, "company-history-bod")}
      </div>
      <ul className="max-h-56 divide-y divide-slate-100 overflow-y-auto rounded-sm border border-slate-200 text-xs" data-testid="company-history-timeline">
        {p.timeline.map((l) => (
          <li key={l.id} className="flex items-center gap-2 px-2 py-1">
            <span className="w-24 shrink-0 rounded bg-slate-100 px-1 text-center text-[10px] text-slate-600">{KIND[l.kind] || l.kind}</span>
            <span className="flex-1 truncate">{l.project_name || "—"}</span>
            <span className="text-[10px] text-slate-500">{l.role}</span>
            {l.status && <span className="text-[10px] text-slate-500">· {l.status}</span>}
            <span className="w-16 text-right text-[10px] text-slate-400">{when(l.at)}</span>
          </li>))}
        {!p.timeline.length && <li className="px-2 py-2 text-slate-400">Nothing linked yet — bids, budgets, estimates, pipeline, BOD rows and POs appear here.</li>}
      </ul>
    </div>
  );
}
